import { useGetCurrencySymbol } from 'app/appState';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { RentalItem } from 'entities/RentalItem';
import { FC, memo, useMemo } from 'react';
import { BulletsTable, TableRow } from 'shared/ui/BulletsTable';
import { roundNumber } from 'shared/utils/helpers/roundNumber';

interface RentailItemsPriceDetailsTableProps {
  rentalItems: RentalItem[];
}

export const RentailItemsPriceDetailsTable: FC<RentailItemsPriceDetailsTableProps> = memo((props) => {
  const { rentalItems } = props;

  const { t } = useAppTranslation('booking');

  const currencySymbol = useGetCurrencySymbol();

  const priceDetailsBullets = useMemo(() => {
    const tableRows: TableRow[] = [];

    rentalItems?.forEach((rentalItem) => {
      const basePrice = roundNumber(rentalItem.price * rentalItem.quantity);
      const discountAmount = roundNumber(basePrice * (rentalItem.discountPercent / 100));
      const priceAfterDiscount = roundNumber(basePrice - discountAmount);
      const vatAmount = roundNumber(priceAfterDiscount * (rentalItem.vatRatePercent / 100));

      tableRows.push({
        label: `${t(rentalItem.rentalItemType)}`,
        value:
          rentalItem.quantity > 1
            ? `${rentalItem.quantity} × ${rentalItem.price} ${currencySymbol}`
            : `${rentalItem.price} ${currencySymbol}`,
      });

      tableRows.push({
        label: t('Discount ({{discountPercent}}%)', { discountPercent: rentalItem.discountPercent || 0 }),
        value: `${discountAmount} ${currencySymbol}`,
      });

      tableRows.push({
        label: t('VAT ({{vatRatePercent}}%)', { vatRatePercent: rentalItem.vatRatePercent || 0 }),
        value: `${vatAmount} ${currencySymbol}`,
      });

      tableRows.push({
        label: `${t('Total for')} ${t(rentalItem.rentalItemType)}`,
        value: `${rentalItem.totalPrice} ${currencySymbol}`,
        highlighted: true,
        color: 'text-accent',
      });
    });

    if (rentalItems.length > 1) {
      const totalToPay = roundNumber(rentalItems.reduce((sum: number, item: RentalItem) => sum + Number(item.totalPrice || 0), 0));

      tableRows.push({
        label: t('Total for rental items'),
        value: `${totalToPay} ${currencySymbol}`,
        highlighted: true,
      });
    }

    return tableRows;
  }, [rentalItems, t, currencySymbol]);

  return (
    <div className="flex flex-col bg-secondaryLight rounded-lg">
      <BulletsTable heading={t('Rental items price details')} rows={priceDetailsBullets} labelClassName="max-w-[220px]" />
    </div>
  );
});
